import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { api, ApiError, type AppVersion } from "../api.js";
import { useAuth, useToken } from "../auth.js";
import { Badge } from "../components/Badge.js";
import { GroupButton } from "../components/GroupButton.js";

type Channel = "stable" | "beta" | "canary";

export function Releases() {
  const token = useToken();
  const { user, emergency } = useAuth();
  // Resellers get a read-only changelog — no download URL, no sha256, no
  // publish/edit/archive controls. Only "what's new" info they can relay to
  // their customers.
  const canManage = emergency || user?.role !== "reseller";
  const { t, i18n } = useLingui();
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [editing, setEditing] = useState<AppVersion | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.listAppVersions(token);
      setVersions(res.appVersions);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeVersions = useMemo(
    () => versions.filter((v) => !v.archivedAt),
    [versions],
  );
  const archivedVersions = useMemo(
    () => versions.filter((v) => !!v.archivedAt),
    [versions],
  );

  const archive = async (v: AppVersion) => {
    if (
      !confirm(
        t`Archive version ${v.version} on ${v.channel}? Agents will stop seeing it as the latest release.`,
      )
    )
      return;
    setBusyId(v.id);
    try {
      await api.archiveAppVersion(token, v.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const unarchive = async (v: AppVersion) => {
    if (!confirm(t`Unarchive version ${v.version} on ${v.channel}?`)) return;
    setBusyId(v.id);
    try {
      await api.unarchiveAppVersion(token, v.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const renderRow = (v: AppVersion) => (
    <tr key={v.id}>
      <td>
        <strong>v{v.version}</strong>
        {v.releaseNotes && (
          <div className="muted" title={v.releaseNotes}>
            {truncate(v.releaseNotes, 80)}
          </div>
        )}
      </td>
      <td>
        <Badge tone={v.channel === "stable" ? "ok" : "warn"}>{v.channel}</Badge>
      </td>
      <td>{v.required ? t`yes` : t`no`}</td>
      <td>{new Date(v.publishedAt).toLocaleString(i18n.locale)}</td>
      {canManage && (
        <>
          <td className="mono short-id" title={v.sha256}>
            {v.sha256.slice(0, 12)}…
          </td>
          <td>
            <a href={v.downloadUrl} target="_blank" rel="noreferrer">
              <Trans>open</Trans>
            </a>
          </td>
          <td>
            <GroupButton
              label={<Trans>Edit</Trans>}
              dropdownLabel={t`Release actions`}
              onClick={() => setEditing(v)}
            >
              <button className="link-btn" onClick={() => setEditing(v)}>
                <Trans>Edit</Trans>
              </button>
              {v.archivedAt ? (
                <button
                  className="link-btn"
                  disabled={busyId === v.id}
                  onClick={() => void unarchive(v)}
                >
                  <Trans>Unarchive</Trans>
                </button>
              ) : (
                <button
                  className="link-btn danger"
                  disabled={busyId === v.id}
                  onClick={() => void archive(v)}
                >
                  <Trans>Archive</Trans>
                </button>
              )}
            </GroupButton>
          </td>
        </>
      )}
    </tr>
  );

  const renderTable = (rows: AppVersion[]) => (
    <table className="table">
      <thead>
        <tr>
          <th><Trans>Version</Trans></th>
          <th><Trans>Channel</Trans></th>
          <th><Trans>Required</Trans></th>
          <th><Trans>Published</Trans></th>
          {canManage && (
            <>
              <th><Trans>SHA-256</Trans></th>
              <th><Trans>Download URL</Trans></th>
              <th />
            </>
          )}
        </tr>
      </thead>
      <tbody>{rows.map(renderRow)}</tbody>
    </table>
  );

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Releases</Trans></h1>
        {canManage && (
          <button onClick={() => setPublishing(true)}>
            <Trans>Publish new version</Trans>
          </button>
        )}
      </header>

      {loadError && <div className="banner err">{loadError}</div>}

      {loading ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : versions.length === 0 ? (
        <p className="muted"><Trans>No app versions published yet.</Trans></p>
      ) : (
        <>
          <h2><Trans>Active releases</Trans></h2>
          {activeVersions.length > 0 ? (
            renderTable(activeVersions)
          ) : (
            <p className="muted"><Trans>No active releases.</Trans></p>
          )}
          <h2 style={{ marginTop: 24 }}><Trans>Archived releases</Trans></h2>
          {archivedVersions.length > 0 ? (
            renderTable(archivedVersions)
          ) : (
            <p className="muted"><Trans>No archived releases.</Trans></p>
          )}
        </>
      )}

      {publishing && (
        <ReleaseEditor
          onCancel={() => setPublishing(false)}
          onSaved={() => {
            setPublishing(false);
            void refresh();
          }}
        />
      )}
      {editing && (
        <ReleaseEditor
          release={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function ReleaseEditor({
  release,
  onCancel,
  onSaved,
}: {
  release?: AppVersion;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const { t } = useLingui();
  const isEdit = !!release;
  const [version, setVersion] = useState(release?.version ?? "");
  const [channel, setChannel] = useState<Channel>(
    (release?.channel as Channel | undefined) ?? "stable",
  );
  const [downloadUrl, setDownloadUrl] = useState(release?.downloadUrl ?? "");
  const [sha256, setSha256] = useState(release?.sha256 ?? "");
  const [required, setRequired] = useState(release?.required ?? false);
  const [releaseNotes, setReleaseNotes] = useState(release?.releaseNotes ?? "");
  const [signature, setSignature] = useState(release?.signature ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && release) {
        await api.updateAppVersion(token, release.id, {
          downloadUrl: downloadUrl.trim(),
          sha256: sha256.trim().toLowerCase(),
          signature: signature.trim() ? signature.trim() : null,
          required,
          releaseNotes: releaseNotes.trim() ? releaseNotes.trim() : null,
        });
      } else {
        await api.createAppVersion(token, {
          version: version.trim(),
          channel,
          downloadUrl: downloadUrl.trim(),
          sha256: sha256.trim().toLowerCase(),
          signature: signature.trim() || undefined,
          required,
          releaseNotes: releaseNotes.trim() || undefined,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>
          {isEdit ? <Trans>Edit release</Trans> : <Trans>Publish new version</Trans>}
        </h2>
        <p className="muted">
          <Trans>
            The download URL must serve the exact bytes hashed by <code>sha256</code>.
            Agents refuse to install any package whose SHA-256 does not match.
          </Trans>
        </p>
        <form onSubmit={submit}>
          <label>
            <Trans>Version (semver, e.g. 0.2.0)</Trans>
            <input
              required
              value={version}
              disabled={isEdit}
              placeholder="0.2.0"
              onChange={(e) => setVersion(e.target.value)}
            />
          </label>
          <label>
            <Trans>Channel</Trans>
            <select
              value={channel}
              disabled={isEdit}
              onChange={(e) => setChannel(e.target.value as Channel)}
            >
              <option value="stable">stable</option>
              <option value="beta">beta</option>
              <option value="canary">canary</option>
            </select>
          </label>
          <label>
            <Trans>Download URL</Trans>
            <input
              required
              type="url"
              value={downloadUrl}
              placeholder="https://cdn.example.com/sagemcp-0.2.0.zip"
              onChange={(e) => setDownloadUrl(e.target.value)}
            />
          </label>
          <label>
            <Trans>SHA-256 (64 hex chars)</Trans>
            <input
              required
              value={sha256}
              placeholder={t`ab12…64 hex`}
              onChange={(e) => setSha256(e.target.value)}
              pattern="[a-fA-F0-9]{64}"
            />
          </label>
          <label>
            <Trans>Signature (optional, base64)</Trans>
            <input value={signature} onChange={(e) => setSignature(e.target.value)} />
          </label>
          <label>
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            <Trans>Mark as required (agents will highlight this in the UI)</Trans>
          </label>
          <label>
            <Trans>Release notes (Markdown)</Trans>
            <textarea
              rows={5}
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder={t`What's new in this release`}
            />
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? (
                <Trans>Saving…</Trans>
              ) : isEdit ? (
                <Trans>Save</Trans>
              ) : (
                <Trans>Publish</Trans>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}
